import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Instagram, Mail, Calendar, Award, Target, Clock, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const trainers = [
  {
    name: 'Rebekka Schulze',
    role: 'Personal Trainer',
    specialties: ['Strength Training', 'Weight Loss & Nutrition', 'Sports Performance', 'Muscle Gain', 'Gym Confidence'],
    experience: '7 years',
    certifications: ['Personal Trainer', 'Nutrition Coach', 'Performance Specialist'],
    availability: 'Mon-Fri: 8am-4pm',
    image: 'https://i.ibb.co/cKfGvL61/5.jpg',
    instagram: '#',
    email: 'rebekkaschulze123@gmail.com',
    bio: 'I had always trained with the purpose of gaining weight unlike how society taught me. With this mindset being the foundation of my training philosophy, I can help you gain the confidence you need to achieve your fitness goals.'
  },
  {
    name: 'Taylor Blackler',
    role: 'Owner',
    specialties: ['Hypertrophy Training', 'Functional Training', 'Nutrition Programming', 'Weight Loss', 'Strength Training'],
    experience: '6 years',
    certifications: ['Personal Trainer', 'Nutrition Coach', 'Strength & Conditioning'],
    availability: 'Mon-Fri: 6am-2pm',
    image: 'https://i.ibb.co/27wvFxjc/3.jpg',
    instagram: '#',
    email: 'taylor@thefitnessportal.co.nz',
    bio: 'While learning the tricks of the trade, I started to enjoy training others and helping my friends get bigger and stronger which opened my eyes to personal training.'
  },
  {
    name: 'Madeleine Collins',
    role: 'Personal Trainer',
    specialties: ['Powerlifting Preparation', 'Strength Training', 'Hypertrophy Training', 'General Health & Fitness'],
    experience: '5 years',
    certifications: ['Personal Trainer', 'Powerlifting Coach', 'Strength & Conditioning'],
    availability: 'Mon-Sat: 12pm-8pm',
    image: 'https://i.ibb.co/BV7Znj4Q/7.jpg',
    instagram: '#',
    email: 'madeleinecollins4@gmail.com',
    bio: 'I started out 30kg heavier and hated the gym! I\'ve dealt with the hurdles you might be facing first-hand. Now I\'m here having achieved my gym dreams and I can help you achieve yours.'
  },
  {
    name: 'Yousef',
    role: 'Personal Trainer',
    specialties: ['Fitness Programming', 'Strength Training', 'Hypertrophy Training', 'Nutritional Guidance', 'Injury Prevention'],
    experience: '8 years',
    certifications: ['Personal Trainer', 'Registered Pharmacist', 'IFBB Pro'],
    availability: 'Mon-Fri: 7am-3pm',
    image: 'https://i.ibb.co/4gRjD6x2/6.jpg',
    instagram: '#',
    email: 'info@ironlionfitness.co.nz',
    bio: 'I\'m a Certified Personal Trainer, a Registered Pharmacist, and an IFBB Pro bodybuilder. I\'ve always been passionate about health and fitness, and I\'ve made it my life\'s mission to share this passion with others.'
  },
  {
    name: 'Tim Fox',
    role: 'Personal Trainer',
    specialties: ['Building Muscle & Fat Loss', 'Shoulder/Knee Rehab', 'Nutrition Plans', 'Sport Specific / Athletes'],
    experience: '10+ years',
    certifications: ['Personal Trainer', 'Rehabilitation Specialist', 'Sports Nutrition'],
    availability: 'Mon-Fri: 6am-2pm',
    image: 'https://i.ibb.co/LDQz9Rhs/4.jpg',
    instagram: '#',
    email: 'tim@thefitnessportal.co.nz',
    bio: 'I developed a love for helping people achieve their goals, which drove me to pursue a career coaching and training all sorts of people, whether it is assisting just with online training, perfecting technique, fat loss, muscle gain, sport specific or bodybuilding, nutrition and dieting.'
  },
  {
    name: 'Michael Froggatt',
    role: 'Personal Trainer',
    specialties: ['Muscle & Strength Training', 'Weight Management', 'Cardiovascular Training', 'Functional Movements'],
    experience: '5+ years',
    certifications: ['Personal Trainer', 'Strength & Conditioning Coach'],
    availability: 'Mon-Fri: 9am-5pm',
    image: 'https://i.ibb.co/Q7f653qn/DSC07327.jpg',
    instagram: '#',
    email: 'mfroggattfitness@gmail.com',
    bio: 'Dedicated to helping clients achieve their fitness goals through personalized training programs focusing on strength, muscle building, and functional movement patterns.'
  }
];

export default function TrainersPage() {
  const [selectedTrainer, setSelectedTrainer] = useState<typeof trainers[0] | null>(null);
  const [hoveredTrainer, setHoveredTrainer] = useState<number | null>(null);

  const navigateTrainer = (direction: 'prev' | 'next') => {
    if (!selectedTrainer) return;
    const currentIndex = trainers.findIndex(t => t.name === selectedTrainer.name);
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? trainers.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === trainers.length - 1 ? 0 : currentIndex + 1;
    }
    setSelectedTrainer(trainers[newIndex]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      <Navbar />
      
      <main className="pt-20">
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-3xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, delay: 1.5, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full blur-3xl"
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl sm:text-7xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                MEET OUR TEAM
              </h1>
              <p className="text-base sm:text-xl text-gray-400">Expert guidance for your fitness journey</p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
              {trainers.map((trainer, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="group cursor-pointer relative"
                  onClick={() => setSelectedTrainer(trainer)}
                  onMouseEnter={() => setHoveredTrainer(index)}
                  onMouseLeave={() => setHoveredTrainer(null)}
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    animate={{
                      scale: hoveredTrainer === index ? 1.02 : 1,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                    <div className="aspect-[3/4] w-full">
                      <img
                        src={trainer.image}
                        alt={trainer.name}
                        className="w-full h-full object-cover object-center transform group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                    </div>
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 p-2 sm:p-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <h3 className="text-sm sm:text-xl font-bold mb-0.5 sm:mb-1 line-clamp-1">{trainer.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-300 mb-1 sm:mb-2 line-clamp-1">{trainer.role}</p>
                      <div className="flex flex-wrap gap-1">
                        {trainer.specialties.slice(0, 2).map((specialty, i) => (
                          <span
                            key={i}
                            className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs line-clamp-1"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <AnimatePresence>
        {selectedTrainer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-lg z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelectedTrainer(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-md rounded-2xl p-4 sm:p-8 w-full max-w-6xl border border-white/10 relative"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTrainer(null);
                }}
                className="absolute top-4 right-4 bg-white/10 p-2 rounded-full hover:bg-white/20 transition-colors z-50"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="relative rounded-xl overflow-hidden group">
                  <div className="relative w-full h-[50vh] lg:h-[60vh]">
                    <img
                      src={selectedTrainer.image}
                      alt={selectedTrainer.name}
                      className="absolute inset-0 w-full h-full object-cover object-center transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="flex space-x-4">
                      <a
                        href={selectedTrainer.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm p-3 rounded-full hover:from-blue-500/30 hover:to-cyan-500/30 transition-colors"
                      >
                        <Instagram className="h-5 w-5" />
                      </a>
                      <a
                        href={`mailto:${selectedTrainer.email}`}
                        className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm p-3 rounded-full hover:from-blue-500/30 hover:to-cyan-500/30 transition-colors"
                      >
                        <Mail className="h-5 w-5" />
                      </a>
                      <a
                        href="#contact"
                        className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm p-3 rounded-full hover:from-blue-500/30 hover:to-cyan-500/30 transition-colors"
                      >
                        <Calendar className="h-5 w-5" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 sm:space-y-8">
                  <div>
                    <h3 className="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                      {selectedTrainer.name}
                    </h3>
                    <p className="text-xl sm:text-2xl text-gray-300 mb-6">{selectedTrainer.role}</p>
                    <p className="text-gray-400 text-base sm:text-lg leading-relaxed">{selectedTrainer.bio}</p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center space-x-2 mb-4">
                        <Target className="h-6 w-6 text-blue-400" />
                        <h4 className="text-lg sm:text-xl font-semibold">Specialties</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedTrainer.specialties.map((specialty, i) => (
                          <span
                            key={i}
                            className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center space-x-2 mb-4">
                        <Award className="h-6 w-6 text-cyan-400" />
                        <h4 className="text-lg sm:text-xl font-semibold">Certifications</h4>
                      </div>
                      <ul className="space-y-2 text-gray-300">
                        {selectedTrainer.certifications.map((cert, i) => (
                          <li key={i} className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                            <span>{cert}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <div className="flex items-center space-x-2 mb-4">
                        <Clock className="h-6 w-6 text-blue-400" />
                        <h4 className="text-lg sm:text-xl font-semibold">Availability</h4>
                      </div>
                      <p className="text-gray-300">{selectedTrainer.availability}</p>
                    </div>
                  </div>

                  <div className="pt-8 flex justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateTrainer('prev');
                      }}
                      className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm p-3 rounded-full hover:from-blue-500/30 hover:to-cyan-500/30 transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateTrainer('next');
                      }}
                      className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm p-3 rounded-full hover:from-blue-500/30 hover:to-cyan-500/30 transition-colors"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}